// Card 59, the sonar sweep (R548, ui-map-interface.md §17.3.59).
//
// The scope sweeps: a bright radius turns, the water behind it glows and
// fades, and a contact flares as the radius crosses its bearing. Here the
// scan reveal of a Vedette (R412) wears that sweep for the minutes it holds,
// so a reader sees that a reveal is LIVE, and roughly how much of it is
// left, without a timer.
//
// **Modern only.** The historical reading keeps the plain reveal ring the
// sonar layer already draws, because the ear has no sweep. The gate does
// that on its own: `[chart.piece.sonar_sweep.historical] on = false`.
//
// **Own reveals only.** The reveal is this commander's own scan; a sweep
// over somebody else's would be a claim about a scan the wire never sent.
//
// The afterglow is the one per-pixel term of the whole chart, and it draws
// through `drawExtra` because no instance batch can carry a conic ramp. The
// flares, the ring and the label are ordinary batch work.

import { chart as S } from '../../../gen/strings';
import { CHART } from '../../../gen/presentation';
import { rgb } from '../../../render/color';
import { leftSpan } from '../../../locale/span';
import { factionColor } from '../../../world';
import { SweepPass, type Sweep, type SweepStyle } from '../../gfx/sweep';
import type { DrawCtx, FrameInfo } from '../../layer';
import { CHART_INK } from './shared';
import type {
  ChartContext, ChartSinks, Piece,
} from '../context';

/** One running reveal, as the label states it. */
interface Reveal {
  x: number;
  y: number;
  /** Map seconds the reveal still holds. */
  leftSec: number;
  /** How many contacts stand inside this reveal's ring. */
  painted: number;
}

/** One contact the radius paints, with the age of that pass. */
interface Painted {
  x: number;
  y: number;
  color: number;
  /** 1 the moment the radius crosses it, falling to 0 over one turn. */
  k: number;
}

/** The afterglow's colour: the accent as three floats in 0 to 1, for the pass. */
const ACCENT_RGB: readonly [number, number, number] = rgb(CHART_INK.accent);

/** The ramp and the radius of the pack, read one time: no frame allocates them. */
const STYLE: SweepStyle = {
  stopAt: CHART.pieces.sonar_sweep.arr.stop_at,
  stopAlpha: CHART.pieces.sonar_sweep.arr.stop_alpha,
  lineAlpha: CHART.pieces.sonar_sweep.fig.line_alpha,
  lineWidth: CHART.pieces.sonar_sweep.fig.line_w,
};

export class SonarSweepPiece implements Piece {
  readonly id = 'sonar_sweep' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = [] as const;

  private readonly pass = new SweepPass();
  /** This frame's sweeps and flares, reused so no frame allocates. */
  private readonly sweeps: Sweep[] = [];
  private readonly painted: Painted[] = [];
  private readonly reveals: Reveal[] = [];

  rebuild(_ctx: ChartContext): void { /* the reveals are read at every pack */ }

  /**
   * Gathers this frame's reveals and what each one paints.
   *
   * The radius turns once every `turn_sec` seconds of the frame clock, which
   * is the WALL clock: the sweep is a picture of a live thing, and it must
   * turn at the same rate whatever timescale the world runs at.
   */
  update(ctx: ChartContext, _frame: FrameInfo): void {
    const fig = ctx.cfg.pieces.sonar_sweep.fig;
    this.sweeps.length = 0;
    this.painted.length = 0;
    this.reveals.length = 0;
    const layer = ctx.scene.fleets;
    if (!layer) return;
    const angle = ((ctx.timeS * Math.PI * 2) / fig.turn_sec) % (Math.PI * 2);
    const fleets = layer.chartFleets();
    for (const f of fleets) {
      if (!f.own || f.scanReadySec <= 0 || f.sonarPx <= 0) continue;
      this.sweeps.push({ x: f.x, y: f.y, r: f.sonarPx, angle, rgb: ACCENT_RGB });
      let painted = 0;
      for (const c of fleets) {
        if (c.own || !c.classified) continue;
        const dx = c.x - f.x, dy = c.y - f.y;
        if (Math.hypot(dx, dy) > f.sonarPx) continue;
        // How long ago the radius passed this bearing, as a fraction of a
        // whole turn: 1 at the pass, 0 one turn later.
        const ca = Math.atan2(dy, dx);
        const since = (((angle - ca) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        this.painted.push({
          x: c.x, y: c.y, color: factionColor(c.faction),
          k: 1 - since / (Math.PI * 2),
        });
        painted++;
      }
      this.reveals.push({ x: f.x, y: f.y, leftSec: f.scanReadySec, painted });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    if (this.sweeps.length === 0) return;
    const t = ctx.cfg.pieces.sonar_sweep;
    const fig = t.fig;
    const r = t.modern;
    for (const p of this.painted) {
      sinks.circles.disc(
        p.x, p.y, fig.flare_r0 + fig.flare_r1 * p.k, p.color, fig.flare_alpha * p.k,
      );
      sinks.circles.ring(
        p.x, p.y, fig.ring_r, r.width, p.color, fig.ring_alpha0 + fig.ring_alpha1 * p.k,
      );
    }
    // One label a reveal: how long it still holds, and how much it holds.
    // The wire pays the reveal on the map clock, and the label reads it in
    // world time like every other duration on screen (R591), with its units
    // (R2741).
    for (const v of this.reveals) {
      ctx.glyphs.word(
        sinks.quads,
        S.sweep_reveal({
          left: leftSpan(ctx.state.mapToWorldSec(v.leftSec)), painted: v.painted,
        }),
        v.x + fig.label_dx, v.y + fig.label_dy,
        'mono', t.text.main, CHART_INK.accent, r.alpha,
      );
    }
  }

  /** This frame's sweeps, as the pass takes them. The test reads it. */
  get sweepsForTest(): readonly Sweep[] { return this.sweeps; }

  /** The afterglow: one quad a reveal, over the batches of this tier. */
  drawExtra(ctx: DrawCtx, fade: number): void {
    if (this.sweeps.length === 0) return;
    this.pass.draw(ctx.gfx, ctx.cam, this.sweeps, STYLE, fade);
  }

  dispose(): void { this.pass.dispose(); }
}

export const sonarSweep = new SonarSweepPiece();
